import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import { contactInputSchema, type ContactDto, type ContactInput } from '@lawyer/shared'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import type { z } from 'zod'
import { api } from '../../lib/api'
import { formResolver } from '../../lib/form'
import { ErrorAlert } from '../common/Feedback'

type FormValues = z.input<typeof contactInputSchema>

const EMPTY: FormValues = { name: '', email: '' }

type Props = {
  open: boolean
  contact?: ContactDto | null
  /** Seeds the name when creating — what was typed into the picker that opened this. */
  defaultName?: string
  onClose: () => void
  onSaved?: (contact: ContactDto) => void
}

export function ContactDialog({ open, contact, defaultName = '', onClose, onSaved }: Props) {
  const queryClient = useQueryClient()
  const isEdit = Boolean(contact)

  const form = useForm<FormValues, unknown, ContactInput>({
    resolver: formResolver(contactInputSchema),
    defaultValues: EMPTY,
  })

  useEffect(() => {
    if (!open) return
    form.reset(
      contact ? { name: contact.name, email: contact.email ?? '' } : { ...EMPTY, name: defaultName }
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, contact?.id, defaultName])

  const save = useMutation({
    mutationFn: (values: ContactInput) =>
      isEdit
        ? api.patch<ContactDto>(`/contacts/${contact?.id}`, values)
        : api.post<ContactDto>('/contacts', values),
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ['contacts'] })
      void queryClient.invalidateQueries({ queryKey: ['project'] })
      void queryClient.invalidateQueries({ queryKey: ['case'] })
      onSaved?.(saved)
      onClose()
    },
  })

  const { errors } = form.formState

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <form onSubmit={form.handleSubmit((values) => save.mutate(values))} noValidate>
        <DialogTitle>{isEdit ? 'עריכת איש קשר' : 'איש קשר חדש'}</DialogTitle>
        <DialogContent dividers>
          {save.error ? <ErrorAlert error={save.error} /> : null}
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <TextField
              label="שם"
              autoFocus
              required
              {...form.register('name')}
              error={Boolean(errors.name)}
              helperText={errors.name?.message}
            />
            <TextField
              label="דואר אלקטרוני"
              type="email"
              {...form.register('email')}
              error={Boolean(errors.email)}
              helperText={errors.email?.message}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>ביטול</Button>
          <Button type="submit" variant="contained" disabled={save.isPending}>
            שמירה
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
